import type { DeliveryResult, SendMessageResponse } from '@/api/types'

/** Which addressing mode the composer used. Mirrors `SendMessageRequest`'s "one or the other". */
export type SendMode = 'connections' | 'channel'

/** What the composer should do with a 200 that may or may not have reached anybody. */
export interface SendOutcome {
  /**
   * Parties nothing was written for: `outcome` is `unknown` (no such party in this zone) or
   * `failed` (refused for a stated reason). Normally empty for a channel send — a member is by
   * definition a known party — but read for both modes, so a refusal is never hidden.
   */
  undelivered: DeliveryResult[]
  /**
   * Parties the message was written for who are not here to read it yet: reserved identities that
   * are offline (`queued_offline`). These ARE sent — the row waits in their queue until they
   * reconnect with their id — so they never keep the text in the box. The composer names them only
   * so the sender knows not to expect an answer soon.
   */
  waitingOffline: DeliveryResult[]
  /** A channel broadcast with no other member at all: no rows were written. */
  nobodyInChannel: boolean
  /** Whether the text may be cleared, i.e. whether at least one row was written for somebody. */
  clearBody: boolean
}

/** A row exists for this party — what the old boolean meant, under either of its two new names. */
function isWritten(entry: DeliveryResult): boolean {
  return entry.outcome === 'queued' || entry.outcome === 'queued_offline'
}

/**
 * Reads a 200 honestly. **A 200 is not proof of delivery.**
 *
 * `SendMessageResponse` reports one `outcome` per addressed party, and IMPROVEMENTS_1 (WI3)
 * changed what the console must say about them:
 *
 * - **`queued`** — written for a live recipient. Sent.
 * - **`queued_offline`** — written for a reserved identity that is offline. Also sent: the queue
 *   survives a disconnect now, so the row waits for the identity's return. Before WI3 an offline
 *   name was simply unknown and the send failed; treating this outcome as a failure would make the
 *   composer refuse to clear a message the server has already committed to deliver.
 * - **`unknown`** — no such party in this zone: nobody connected or reserved under that name, or a
 *   released one. Nothing written.
 * - **`failed`** — refused for a stated reason (yourself, an empty name). Nothing written.
 *
 * Partial delivery is success (§6): naming three parties of which one is unknown writes two rows
 * and reports the third inside the 200. Clearing the box on a send that wrote *nothing* would
 * quietly lose the message, so the text stays exactly when every named party came back
 * `unknown`/`failed`.
 *
 * **A channel with no other member.** `MessageQueueService.enqueueToChannel` excludes the sender
 * from its own fan-out. Offline members now receive a row like anybody else, so the only way it
 * writes nothing is a channel whose sole member is the sender — the state one click on "Leave"
 * produces. That case still leaves no trace: no rows, no fan-out id, no history entry, no
 * conversation segment. This project has already ruled on a silent "sent" (*rather have an error
 * than the fake idea that an email has been sent*), so it is surfaced as `nobodyInChannel` rather
 * than swallowed. `results` now lists every member on a channel send, so "no other member" is
 * exactly "zero results" — not `delivered === 0`, which a channel of only offline members no
 * longer produces.
 */
export function sendOutcome(mode: SendMode, result: SendMessageResponse): SendOutcome {
  const undelivered = result.results.filter(
    (entry) => entry.outcome === 'unknown' || entry.outcome === 'failed',
  )
  const waitingOffline = result.results.filter((entry) => entry.outcome === 'queued_offline')
  const written = result.results.filter(isWritten).length

  if (mode === 'channel') {
    const nobodyInChannel = result.results.length === 0
    return { undelivered, waitingOffline, nobodyInChannel, clearBody: written > 0 }
  }
  return {
    undelivered,
    waitingOffline,
    nobodyInChannel: false,
    // At least one row written, or nothing was addressed at all.
    clearBody: written > 0 || result.results.length === 0,
  }
}
